import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Ticket } from "lucide-react";
import { useTenant } from "../../context/TenantContext";
import { useAuth, API } from "../../context/AuthContext";
import ShopHeader from "./ShopHeader";

export default function PublicShop() {
  const [events, setEvents] = useState([]);
  const [shopSettings, setShopSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { tenant } = useTenant();
  const { user, token: userToken } = useAuth();

  useEffect(() => {
    const headers = userToken ? { Authorization: `Bearer ${userToken}` } : {};
    Promise.all([
      axios.get(`${API}/ticketing/public/events`, { headers }).then(r => r.data || []).catch(() => []),
      axios.get(`${API}/ticketing/public/shop-settings`).then(r => r.data || {}).catch(() => ({})),
    ]).then(([ev, ss]) => {
      setEvents(ev); setShopSettings(ss); setLoading(false);
    });
  }, [userToken]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50" data-testid="public-shop">
      <ShopHeader tenantSlug={tenantSlug} />

      <main className="max-w-5xl mx-auto px-4 py-8">
        {shopSettings?.banner_url && (
          <div className="rounded-3xl overflow-hidden mb-8 relative bg-slate-50" data-testid="shop-banner">
            <img
              src={shopSettings.banner_url}
              alt=""
              className="w-full h-auto max-h-[420px] block mx-auto"
              /* iter169: previous handler was `e.target.parentElement.style.display = "none"`,
                 which permanently hid the whole hero on a single transient
                 image failure (cold CDN, one-off 5xx, offline preflight).
                 That was the root cause of the "banner missing on fresh
                 browsers" report — a stale CDN-cached 404 nuked the hero.
                 Now we only hide the broken IMG itself, leaving the hero
                 container + text overlay intact so the page never looks
                 half-built when the banner endpoint blips. */
              onError={(e) => { e.target.style.display = "none"; }}
            />
            {(shopSettings.hero_title || shopSettings.hero_subtitle) && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6 pointer-events-none">
                {shopSettings.hero_title && <h2 className="text-white text-2xl sm:text-3xl font-extrabold">{shopSettings.hero_title}</h2>}
                {shopSettings.hero_subtitle && <p className="text-white/80 mt-1 text-sm">{shopSettings.hero_subtitle}</p>}
              </div>
            )}
          </div>
        )}
        {!shopSettings?.banner_url && (shopSettings?.hero_title || shopSettings?.hero_subtitle) && (
          <div className="mb-8" data-testid="shop-hero-text">
            {shopSettings.hero_title && <h2 className="text-3xl font-extrabold text-[#1e3a5f]">{shopSettings.hero_title}</h2>}
            {shopSettings.hero_subtitle && <p className="text-slate-500 mt-1">{shopSettings.hero_subtitle}</p>}
          </div>
        )}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-[#1e3a5f] mb-2">Upcoming Events</h1>
          <p className="text-slate-500">Browse events and book your tickets.</p>
        </div>

        {loading && <p className="text-slate-400 text-center py-12">Loading events...</p>}
        {(() => {
          const upcoming = events.filter(e => !e.is_past);
          const past = events.filter(e => e.is_past);
          const nothingLive = !loading && upcoming.length === 0 && past.length === 0;
          return (
            <>
              {nothingLive && (
                <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                  <Ticket className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 font-medium">No events available right now</p>
                  <p className="text-slate-400 text-sm mt-1">Check back soon for ticketed events.</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shop-events-grid">
                {upcoming.map(e => (
                  <button
                    key={e.id}
                    onClick={() => navigate(`/${tenantSlug}/event/${e.id}`)}
                    className="text-left bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-slate-100"
                    data-testid={`event-card shop-event-${e.id}`}
                  >
                    {e.marketing_banner_url ? (
                      <img src={e.marketing_banner_url} alt={e.name} className="w-full h-auto max-h-64 block bg-slate-50" />
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-[#1e3a5f] to-[#2d8a4e] flex items-center justify-center">
                        <Ticket className="w-12 h-12 text-white/70" />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-bold text-[#1e3a5f] mb-1">{e.name}</h3>
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                        <Calendar className="w-3 h-3" /> {e.start_date}{e.end_date && e.end_date !== e.start_date ? ` – ${e.end_date}` : ""}
                      </div>
                      {e.location && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <MapPin className="w-3 h-3" /> {e.location}
                        </div>
                      )}
                      <div className="mt-3 text-xs text-[#2d8a4e] font-semibold flex items-center gap-1 flex-wrap" data-testid={`event-counts-${e.id}`}>
                        <span>{e.ticket_types_count} ticket tier{e.ticket_types_count !== 1 ? "s" : ""}</span>
                        {(e.packages_count || 0) > 0 && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span>{e.packages_count} package{e.packages_count !== 1 ? "s" : ""}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {past.length > 0 && (
                <section className="mt-14" data-testid="past-events-section">
                  <div className="mb-4">
                    <h2 className="text-xl font-extrabold text-slate-500">Past Events</h2>
                    <p className="text-xs text-slate-400">Echoes of Great Moments</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
                    {past.map(e => (
                      <div
                        key={e.id}
                        className="text-left bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 cursor-default"
                        data-testid={`past-event-card past-event-${e.id}`}
                        aria-disabled="true"
                      >
                        {e.marketing_banner_url ? (
                          <img src={e.marketing_banner_url} alt={e.name} className="w-full h-auto max-h-64 block grayscale bg-slate-50" />
                        ) : (
                          <div className="w-full h-40 bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center">
                            <Ticket className="w-12 h-12 text-white/70" />
                          </div>
                        )}
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-slate-500">{e.name}</h3>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-500">Past</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-slate-400 mb-1">
                            <Calendar className="w-3 h-3" /> {e.start_date}{e.end_date && e.end_date !== e.start_date ? ` – ${e.end_date}` : ""}
                          </div>
                          {e.location && (
                            <div className="flex items-center gap-1 text-xs text-slate-400">
                              <MapPin className="w-3 h-3" /> {e.location}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          );
        })()}
      </main>
    </div>
  );
}
